import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Add_Bulk_Upload } from '../../../Api';
import { base_url } from '../../../Api/ApiConst';
import { STORAGE_CONSTANTS } from '../../../shared/Constants/Constants';
import BtnLoader from './BtnLoader';

interface BulkUploadFormProps {
  styles: any;
  t: (key: string) => string;
}

interface BulkErrorDetails {
  message: string;
  errorFilePath?: string | null;
  totalFaildProduct?: number | null;
  totalUploadProduct?: number | null;
  createdRecord?: number | null;
  error?: string | null;
}

const ProductBulkUpload: React.FC<BulkUploadFormProps> = ({ styles, t }) => {
  const navigate = useNavigate();
  
  // State management
  const [file, setFile] = useState<File | null>(null);
  const [zip, setZip] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [zipFileName, setZipFileName] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");
  const [isLoading, setLoading] = useState<boolean>(false);
  const [bulkErrorDetails, setBulkErrorDetails] = useState<BulkErrorDetails | null>(null);

  // Get user data from localStorage
  const userToken = localStorage.getItem(STORAGE_CONSTANTS.USER_TOKEN);
  const storeId = localStorage.getItem(STORAGE_CONSTANTS.STORE_ID);

  // File type detection
  const getFileExtension = (filename: string): void => {
    const parts = filename.split(".");
    const fileType = parts.pop();
    if (fileType === "xls" || fileType === "xlsx") {
      setFileType("excel");
    } else if (fileType === "csv") {
      setFileType("csv");
    }
  };

  // File upload handlers
  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0];
    
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      getFileExtension(selectedFile.name);
    }
    e.preventDefault();
  };

  const handleBulkZipChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0];
    
    if (selectedFile) {
      setZip(selectedFile);
      setZipFileName(selectedFile.name);
    }
    e.preventDefault();
  };

  // Reset form function
  const resetForm = (): void => {
    setFile(null);
    setZip(null);
    setFileType("");
    setFileName("");
    setZipFileName("");
  };

  // Success message handler
  const showToastMessage = (response: any): void => {
    const { created, repeated } = response;

    const repeatedProductText = repeated > 1 ? "Products" : "Product";
    const createdProductText = created > 1 ? "Products" : "Product";

    if (created > 0 && repeated === 0) {
      toast.success("Bulk upload successful");
    } else if (created > 0 && repeated > 0) {
      toast.success(
        `${created} new ${createdProductText} added, ${repeated} ${repeatedProductText} repeated`
      );
    } else if (created === 0 && repeated > 0) {
      toast.error(`${repeated} ${repeatedProductText} repeated`);
    }
  };

  // Main form submission handler
  const handleFormSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    const BulkProduct = async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await Add_Bulk_Upload(
          userToken,
          storeId,
          file,
          zip,
          fileType
        );

        // Check for status code 202 (successful upload)
        if (response.status === 202) {
          showToastMessage(response.data);

          // If the bulk upload completed with errors
          if (response.data.errors && response.data.error_file_path) {
            setBulkErrorDetails({
              message: response.data.message,
              errorFilePath: response.data.error_file_path,
              totalFaildProduct: response.data.total_faild_product,
              totalUploadProduct: response.data.total_upload_product,
              createdRecord: response.data.created,
              error: response.data.error,
            });
          }

          navigate("/products");
          resetForm();
        }
        // Handle case for 400 status (Bad Request)
        else if (response.status === 400) {
          setBulkErrorDetails({
            message: response.data.message || "Bulk upload completed with errors.",
            error: response.data.error,
            errorFilePath: response.data.error_file_path || null,
            totalFaildProduct: response.data.total_faild_product || null,
            totalUploadProduct: response.data.total_upload_product || null,
            createdRecord: response.data.created || null,
          });
        }
        // Handle any other unexpected status codes
        else {
          setBulkErrorDetails({
            message: "Bulk upload failed with an unexpected error.",
            errorFilePath: null,
            totalFaildProduct: null,
            totalUploadProduct: null,
            createdRecord: null,
            error: null,
          });
        }
      } catch (error: any) {
        console.error("Error occurred:", error.response?.data || error);
        
        if (error.response?.data) {
          // Specific error message and details
          setBulkErrorDetails({
            message: error.response?.data.message || "Error occurred during bulk upload.",
            errorFilePath: error.response?.data.error_file_path || null,
            totalFaildProduct: error.response?.data.total_faild_product || null,
            totalUploadProduct: error.response?.data.total_upload_product || null,
            createdRecord: error.response?.data.created || null,
            error: error.response.data.error || null,
          });
        } else {
          setBulkErrorDetails({
            message: "An unexpected error occurred during the upload.",
            errorFilePath: null,
            totalFaildProduct: null,
            totalUploadProduct: null,
            createdRecord: null,
            error: null,
          });
        }
        resetForm();
      } finally {
        setLoading(false);
      }
    };

    await BulkProduct();
  };

  return (
    <Row
      className="text-center mt-5"
      style={{ maxWidth: 600, margin: "auto" }}
    >
      <Col>
        <form onSubmit={handleFormSubmit}>
          {/* CSV/Excel File Upload */}
          <div>
            <label
              htmlFor="csvFile"
              className="file-upload-label"
              style={{ ...styles.uploadTxt, cursor: "pointer" }}
            >
              <FontAwesomeIcon icon={faFile as any} className="mx-1" />
              {t("product.upload")}
              <input
                type="file"
                id="csvFile"
                accept=".csv, .xls, .xlsx"
                onChange={handleBulkFileChange}
                style={{ display: "none" }}
              />
            </label>
            <p style={styles.uploadedFile}>{fileName}</p>
          </div>

          {/* Zip File Upload */}
          <div>
            <label
              htmlFor="zipFile"
              className="file-upload-label"
              style={{ ...styles.uploadTxt, cursor: "pointer" }}
            >
              <FontAwesomeIcon icon={faFile as any} className="mx-1" />
              {"Zip File"}
              <input
                type="file"
                id="zipFile"
                accept=".zip"
                onChange={handleBulkZipChange}
                style={{ display: "none" }}
              />
            </label>
            <p style={styles.uploadedFile}>{zipFileName}</p>
          </div>

          {/* Error Details Display */}
          {bulkErrorDetails?.message && (
            <div style={{ ...styles.uploadedFile, color: "red" }}>
              <p>
                <b>{bulkErrorDetails.message}</b>
                <br />
                Total Records: {bulkErrorDetails.totalUploadProduct || 0},
                Success: {bulkErrorDetails.createdRecord || 0},
                Failure: {bulkErrorDetails.totalFaildProduct || 0}
                <br />
                {bulkErrorDetails?.error && (
                  <span style={{ color: "red" }}>
                    Error: {bulkErrorDetails.error}
                  </span>
                )}
                <br />
                {bulkErrorDetails?.errorFilePath && (
                  <a
                    href={bulkErrorDetails.errorFilePath}
                    download="error_log.csv"
                    style={{ textDecoration: "underline" }}
                  >
                    Click here to download file with error records
                  </a>
                )}
              </p>
            </div>
          )}

          {/* Template Download Section */}
          <h3 style={styles.labelText}>{t("product.bulkInfo")}</h3>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon={faFile as any} style={styles.templateIcon} />
              <h3 style={styles.templateText} className="mb-0">
                Template 1
              </h3>
            </div>
            <a href={`${base_url}/api/v0/store/product/bulk_upload/sample`}>
              {t("product.download")}
            </a>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btnBlue submit"
          >
            {isLoading ? <BtnLoader /> : t("commonFormLabels.submit")}
          </button>
        </form>
      </Col>
    </Row>
  );
};

export default ProductBulkUpload;